import React from 'react';
import EmailVerification from '../../components/settings/EmailVerification';
import YalangoComputer from '../../assets/svg/yalango/yalango_computer.svg';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import HelmetData from '../../components/helmet/HelmetData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/pro-solid-svg-icons';

const EmailVerificationPage = () => {
    const {logout} = useAuth();
    const navigate = useNavigate();
    
    const logOutClick = async () => {
        try {
            await logout();
            navigate("/admin");
        } catch {
            // Failed to log out - silently handle
        }
    }

    return (
        <>
            <HelmetData 
                title="Verify Your Email - Yalango" 
                description="Please verify your email address to continue using Yalango." 
            />
            <div className="min-h-screen flex items-center justify-center w-full py-12 px-4">
                <div className="w-full max-w-2xl space-y-8">
                    {/* Header */}
                    <div className="text-center space-y-2">
                        <h1 className="">
                        Email Verification Required
                        </h1>

                    </div>

                    {/* Why Verification is Required */}
                    <div className="bg-white dark:from-gray-900/50 dark:to-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
                        <div className="flex flex-col md:flex-row items-center gap-6">
                            <div className="flex-shrink-0">
                                <img 
                                    src={YalangoComputer} 
                                    alt="Yalango mascot" 
                                    className="w-24 h-24 md:w-28 md:h-28 object-contain" 
                                />
                            </div>
                            <div className="flex-1 space-y-2 text-center md:text-left">

                                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                Please verify your email address to continue. Email verification protects Yalango from spam and unauthorized access, ensuring a safer experience for all users. This step is required before accessing your dashboard.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Verification Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
                        <div className="p-8 space-y-6">
                            {/* Card Header */}
                            <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Verification Status
                                </h2>

                            </div>

                            {/* Email Verification Component */}
                            <div>
                                <EmailVerification />
                            </div>

                            {/* Help Information */}
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50">
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:from-blue-950/30 dark:to-indigo-950/20 rounded-lg p-4 border border-blue-100/50 dark:border-blue-900/30">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                        <span className="font-semibold text-gray-900 dark:text-white">Can&apos;t find the email?</span> Please check your spam or junk folder. 
                                        The verification email may take a few minutes to arrive. Make sure to check the email address you used during registration.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Logout Option */}
                    <div className="text-center pt-4">
                        <div className="inline-flex flex-col items-center space-y-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                                Need to verify later?
                            </p>
                            <button
                                onClick={logOutClick}
                                className="group inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200 font-medium"
                            >
                                <FontAwesomeIcon 
                                    icon={faSignOutAlt} 
                                    className="transition-transform duration-200 group-hover:translate-x-0.5" 
                                />
                                <span className="border-b border-transparent group-hover:border-gray-400 dark:group-hover:border-gray-500 transition-colors duration-200">
                                    Log out and verify later
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Footer Note */}
                    <div className="text-center pt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Need assistance? Contact us at{' '}
                            <a 
                                href="mailto:hello@yalango.com" 
                                className="text-persian-green hover:text-persian-green/80 dark:text-persian-green/90 dark:hover:text-persian-green transition-colors duration-200 font-medium underline decoration-2 underline-offset-2 decoration-persian-green/30 hover:decoration-persian-green/50"
                            >
                                hello@yalango.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}

export default EmailVerificationPage;